import { Capacitor, registerPlugin } from '@capacitor/core';
import appLinks from '../../appLinks/config.json';

const nativeAuth = registerPlugin('WiwGoogleAuth');
export const isNativeGoogleAuth = () => Capacitor.getPlatform() === 'ios'
  || window.location.protocol === 'capacitor:';

export async function createGoogleProof() {
  if (isNativeGoogleAuth()) return nativeAuth.createProof();
  const bytes = window.crypto.getRandomValues(new Uint8Array(32));
  const verifier = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  const digest = await window.crypto.subtle.digest('SHA-256', Uint8Array.from(verifier, char => char.charCodeAt(0)));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return { verifier, challenge };
}

export function googleAuthorizationUrl({ clientId, state, challenge }) {
  const origin = isNativeGoogleAuth() ? appLinks.origin : window.location.origin;
  const redirect = new URL('/login', origin);
  if (!['https:', 'http:'].includes(redirect.protocol)) throw new Error('Invalid OAuth redirect');
  return 'https://accounts.google.com/o/oauth2/v2/auth?' + new URLSearchParams({
    client_id: clientId, redirect_uri: redirect.href, response_type: 'code',
    scope: 'openid profile email', state, nonce: state,
    code_challenge: challenge, code_challenge_method: 'S256',
  });
}

export function readGoogleCallback(address) {
  const url = new URL(address);
  const query = url.searchParams;
  const hash = new URLSearchParams(url.hash.slice(1));
  const params = query.has('code') || query.has('error') ? query : hash;
  if (!params.has('code') && !params.has('id_token') && !params.has('error')) return null;
  const keys = ['code', 'id_token', 'error', 'state'];
  if (keys.some(key => params.getAll(key).length > 1)) return { invalid: true };
  return Object.fromEntries(keys.map(key => [key, params.get(key)]));
}

// A system authentication session reaches HTTPS first. Relay only the short-
// lived code (never tokens or a redirect supplied by the caller) back to iOS.
export function nativeGoogleReturn(callback) {
  if (!callback || !/^ios\.[a-f0-9]{64}$/.test(callback.state || '') || callback.state.length !== 68
    || callback.id_token || callback.invalid || (!callback.code && !callback.error)) return null;
  if (callback.code && (callback.code.length > 4096 || /[^\x21-\x7e]/.test(callback.code))) return null;
  const params = new URLSearchParams({ state: callback.state });
  if (callback.error) params.set('error', 'access_denied');
  else params.set('code', callback.code);
  return `${appLinks.iosScheme}://${new URL(appLinks.origin).host}/login?${params}`;
}

export const openNativeGoogleAuth = (url, state) => nativeAuth.authenticate({ url, state });
export const cancelNativeGoogleAuth = state => nativeAuth.cancel({ state }).catch(() => {});
